import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, Download, Filter, X, Calendar, Ship, BarChart3, Table, ArrowLeft, Save } from 'lucide-react';
import { 
  formatDate, 
  formatNumber, 
  calculateLaycanStatus, 
  getLaycanStatusColor,
  getPortStayDeviationColor,
  intervalToDays,
  dateDiffInDays,
  formatDateForInput,
  getDateRangePreset
} from '../../lib/pcerUtils';
import PCERReportTable from './PCERReportTable';
import PCERDashboard from './PCERDashboard';

export default function PCERReport({ savedReport = null, onSave = null, onBack = null }) {
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('table');
  const [lastSaved, setLastSaved] = useState(null);
  
  // Filter states
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [vesselTypes, setVesselTypes] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [selectedVesselTypes, setSelectedVesselTypes] = useState([]);
  const [selectedVessels, setSelectedVessels] = useState([]);
  const [filteredVessels, setFilteredVessels] = useState([]);

  // Dashboard state (passed to PCERDashboard)
  const [dashboardWidgets, setDashboardWidgets] = useState([]);
  const [dashboardLayouts, setDashboardLayouts] = useState([]);

  // Load saved report configuration
  useEffect(() => {
    if (savedReport) {
      setDateRange(savedReport.config?.dateRange || { startDate: '', endDate: '' });
      setSelectedVesselTypes(savedReport.config?.selectedVesselTypes || []);
      setSelectedVessels(savedReport.config?.selectedVessels || []);
      setActiveTab(savedReport.config?.activeTab || 'table');
      setReportData(savedReport.data || []);
      setDashboardWidgets(savedReport.dashboard?.widgets || []);
      setDashboardLayouts(savedReport.dashboard?.layouts || []);
      setLastSaved(savedReport.updatedAt);
    }
  }, [savedReport]);

  // Check Operations DB connection on mount
  useEffect(() => {
    checkConnection();
    // Set default date range to last 3 months if no saved report
    if (!savedReport) {
      const preset = getDateRangePreset('last-3-months');
      setDateRange(preset);
    }
  }, [savedReport]);

  // Load filter data when connected
  useEffect(() => {
    if (isConnected) {
      loadVesselTypes();
      loadVessels();
    }
  }, [isConnected]);

  // Update filtered vessels when vessel types or all vessels change
  useEffect(() => {
    if (selectedVesselTypes.length > 0) {
      const filtered = vessels.filter(v => 
        selectedVesselTypes.includes(v.vessel_type)
      );
      setFilteredVessels(filtered);
      
      // Remove selected vessels that don't match the type filter
      setSelectedVessels(prev => 
        prev.filter(vesselId => 
          filtered.some(v => v.id === vesselId)
        )
      );
    } else {
      setFilteredVessels(vessels);
    }
  }, [selectedVesselTypes, vessels]);

  const checkConnection = async () => {
    try {
      const sessionId = sessionStorage.getItem('sessionId');
      const response = await fetch('/api/setup/operations-status', {
        headers: { 'x-session-token': sessionId }
      });
      const data = await response.json();
      setIsConnected(data.connected);
    } catch (err) {
      console.error('Connection check error:', err);
      setIsConnected(false);
    }
  };

  const loadVesselTypes = async () => {
    try {
      const sessionId = sessionStorage.getItem('sessionId');
      const response = await fetch('/api/operations/vessel-types', {
        headers: { 'x-session-token': sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setVesselTypes(data);
      }
    } catch (err) {
      console.error('Error loading vessel types:', err);
    }
  };

  const loadVessels = async () => {
    try {
      const sessionId = sessionStorage.getItem('sessionId');
      const response = await fetch('/api/operations/vessels', {
        headers: { 'x-session-token': sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setVessels(data);
        setFilteredVessels(data);
      }
    } catch (err) {
      console.error('Error loading vessels:', err);
    }
  };

  const loadReport = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setError('Please select a date range');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sessionId = sessionStorage.getItem('sessionId');
      const params = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });

      // Add vessel type filter only if no specific vessels selected
      if (selectedVesselTypes.length > 0 && selectedVessels.length === 0) {
        params.append('vesselTypes', selectedVesselTypes.join(','));
      }

      // Add vessel filter (overrides vessel type)
      if (selectedVessels.length > 0) {
        params.append('vessels', selectedVessels.join(','));
      }

      const response = await fetch(`/api/operations/pcer-report?${params}`, {
        headers: { 'x-session-token': sessionId }
      });

      if (!response.ok) {
        throw new Error('Failed to load report');
      }

      const result = await response.json();
      setReportData(result.data || []);
      
      // Auto-save after loading report data
      if (savedReport && onSave) {
        saveReportState(result.data || []);
      }
    } catch (err) {
      console.error('Error loading report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveReportState = (data = reportData) => {
    if (!savedReport || !onSave) return;

    const reportState = {
      config: {
        dateRange,
        selectedVesselTypes,
        selectedVessels,
        activeTab
      },
      data: data,
      dashboard: {
        widgets: dashboardWidgets,
        layouts: dashboardLayouts
      },
      updatedAt: new Date().toISOString()
    };
    
    onSave(reportState);
    setLastSaved(reportState.updatedAt);
  };

  // Auto-save when key state changes
  useEffect(() => {
    if (savedReport) {
      const timer = setTimeout(() => {
        saveReportState();
      }, 2000); // Debounce auto-save by 2 seconds
      
      return () => clearTimeout(timer);
    }
  }, [activeTab, dashboardWidgets, dashboardLayouts]);

  const handleVesselTypeToggle = (typeId) => {
    setSelectedVesselTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(id => id !== typeId);
      } else {
        return [...prev, typeId];
      }
    });
  };

  const handleVesselToggle = (vesselId) => {
    setSelectedVessels(prev => {
      if (prev.includes(vesselId)) {
        return prev.filter(id => id !== vesselId);
      } else {
        return [...prev, vesselId];
      }
    });
  };

  const clearFilters = () => {
    setSelectedVesselTypes([]);
    setSelectedVessels([]);
    const preset = getDateRangePreset('last-3-months');
    setDateRange(preset);
  };

  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV content
    const headers = [
      'Port Operation #',
      'Vessel',
      'Voyage Number',
      'Port/Position',
      'ATA',
      'ATS',
      'Planned Port Days',
      'Actual Port Days',
      'Comments',
      'Laydays',
      'Cancelation',
      'Lay can Deviation (Days)',
      'Lay can Status',
      'Port Stay Deviation (Days)'
    ];

    const rows = reportData.map(row => {
      const actualPortDays = row.actual_port_days !== null ? parseFloat(row.actual_port_days) : null;
      const plannedDays = row.planned_days !== null ? parseFloat(row.planned_days) : 
                          (row.working_days && row.idle_days ? 
                            intervalToDays(row.working_days) + intervalToDays(row.idle_days) : null);
      const portStayDeviation = (actualPortDays !== null && plannedDays !== null) ? 
                                actualPortDays - plannedDays : null;
      const laycanStatus = calculateLaycanStatus(row.ata, row.laycan_start_date, row.laycan_end_date);

      return [
        row.port_operation_number || row.port_operation_id || 'N/A',
        row.vessel_name || 'N/A',
        row.voyage_number || 'N/A',
        row.port_name || 'N/A',
        formatDate(row.ata),
        formatDate(row.ats),
        formatNumber(plannedDays),
        formatNumber(actualPortDays),
        row.remarks || '',
        formatDate(row.laycan_start_date),
        formatDate(row.laycan_end_date),
        formatNumber(row.laycan_deviation_days),
        laycanStatus,
        formatNumber(portStayDeviation)
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `PCER_Report_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    link.click();
  };

  if (!isConnected) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Operations Database Not Connected</h3>
              <p className="text-gray-600 mb-4">
                Please connect to the Operations database to view the PCER Report.
              </p>
              <Button onClick={() => window.location.href = '#operations-db'}>
                Go to Database Browser
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Top Bar with Back Button and Save Status */}
      {(onBack || savedReport) && (
        <div className="flex items-center justify-between px-4 py-2 bg-white border-b">
          <div className="flex items-center gap-4">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Reports
              </Button>
            )}
            {savedReport && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">{savedReport.name}</span>
              </div>
            )}
          </div>
          {savedReport && (
            <div className="flex items-center gap-3">
              {lastSaved && (
                <span className="text-xs text-gray-500">
                  Saved {new Date(lastSaved).toLocaleTimeString()}
                </span>
              )}
              <Button
                size="sm"
                onClick={() => saveReportState()}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Now
              </Button>
            </div>
          )}
        </div>
      )}
      
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Filters */}
        <div className="w-80 border-r bg-gray-50 overflow-y-auto">
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </h2>
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </div>

          {/* Date Range - Mandatory */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Date Range <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
                required
              />
              <div className="text-center text-xs text-gray-500">to</div>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
                required
              />
            </div>
            <div className="mt-2 space-y-1">
              {[
                { label: 'Last Month', value: 'last-month' },
                { label: 'Last 3 Months', value: 'last-3-months' },
                { label: 'Last 6 Months', value: 'last-6-months' },
                { label: 'This Year', value: 'this-year' }
              ].map(preset => (
                <Button
                  key={preset.value}
                  variant="outline"
                  size="sm"
                  onClick={() => setDateRange(getDateRangePreset(preset.value))}
                  className="w-full text-xs"
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Vessel Type - Optional */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
              <Ship className="w-4 h-4" />
              Vessel Type <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <div className="space-y-1 max-h-48 overflow-y-auto border rounded p-2 bg-white">
              {vesselTypes.length === 0 ? (
                <span className="text-gray-500 text-sm">Loading...</span>
              ) : (
                vesselTypes.map(type => (
                  <label key={type.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer rounded">
                    <input
                      type="checkbox"
                      checked={selectedVesselTypes.includes(type.id)}
                      onChange={() => handleVesselTypeToggle(type.id)}
                      className="rounded"
                    />
                    <span className="text-sm">{type.name}</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Filters available vessels below
            </p>
          </div>

          {/* Vessels - Optional */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center gap-2">
              <Ship className="w-4 h-4" />
              Vessels <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <div className="space-y-1 max-h-64 overflow-y-auto border rounded p-2 bg-white">
              {filteredVessels.length === 0 ? (
                <span className="text-gray-500 text-sm">
                  {vessels.length === 0 ? 'Loading...' : 'No vessels match'}
                </span>
              ) : (
                filteredVessels.map(vessel => (
                  <label key={vessel.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer rounded">
                    <input
                      type="checkbox"
                      checked={selectedVessels.includes(vessel.id)}
                      onChange={() => handleVesselToggle(vessel.id)}
                      className="rounded"
                    />
                    <span className="text-sm">{vessel.vessel_name}</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Selected: {selectedVessels.length}
            </p>
          </div>

          {/* Load Button */}
          <Button
            onClick={loadReport}
            disabled={loading || !dateRange.startDate || !dateRange.endDate}
            className="w-full"
          >
            {loading ? 'Loading...' : 'Load Report'}
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header with Tabs */}
        <div className="border-b bg-white">
          <div className="flex items-center justify-between p-4">
            <div>
              <h1 className="text-2xl font-bold">Port Call Evaluation Report (PCER)</h1>
              <p className="text-sm text-gray-600">Operations Database - Voyage Performance Analysis</p>
            </div>
            <Button
              onClick={exportToExcel}
              disabled={reportData.length === 0}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex border-t">
            <button
              onClick={() => setActiveTab('table')}
              className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium transition-colors ${
                activeTab === 'table'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Table className="w-4 h-4" />
              Report Table
            </button>
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium transition-colors ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Report Dashboard
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'table' ? (
            <div className="h-full overflow-auto p-4 space-y-4">
              {/* Error Message */}
              {error && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertCircle className="w-5 h-5" />
                      <span>{error}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Summary Cards */}
              {reportData.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-gray-600">Total Port Calls</div>
                      <div className="text-2xl font-bold">{reportData.length}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-gray-600">Unique Voyages</div>
                      <div className="text-2xl font-bold">
                        {new Set(reportData.map(r => r.voyage_number)).size}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-gray-600">Unique Vessels</div>
                      <div className="text-2xl font-bold">
                        {new Set(reportData.map(r => r.vessel_name)).size}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-gray-600">Avg Actual Port Days</div>
                      <div className="text-2xl font-bold">
                        {formatNumber(
                          reportData.reduce((sum, r) => sum + (parseFloat(r.actual_port_days) || 0), 0) / reportData.length
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Data Table */}
              <PCERReportTable reportData={reportData} loading={loading} />
            </div>
          ) : (
            <PCERDashboard
              reportData={reportData}
              widgets={dashboardWidgets}
              layouts={dashboardLayouts}
              onWidgetsChange={setDashboardWidgets}
              onLayoutsChange={setDashboardLayouts}
            />
          )}
        </div>
      </div>
    </div>
    </div>
  );
}
